'use client';

import { Button, Spinner } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { cn } from '@core/utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';

import { AuthSdk, UnauthSdk } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES, STORAGE_KEY } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

import { ErrorFallback } from './ErrorFallback';
import { FeedCard } from './FeedCard';
import {
  extractSeenIds,
  loadSeenFeedIds,
  loadSeenFeedIdsFromStorage,
  mergeSeenIds,
  saveSeenFeedIds,
  saveSeenFeedIdsToStorage,
  type SeenFeedIds,
  toBodyFilter,
} from './feedSeenIds';
import styles from './FeedSkeleton.module.scss';

const FEED_SIZE = 3;
const SEEN_FEED_IDS_KEY = 'wello-feed-seen-ids';

const isPageReload = () => {
  const navigation = performance.getEntriesByType('navigation').at(0) as
    | PerformanceNavigationTiming
    | undefined;

  return navigation?.type === 'reload';
};

function createFeedStateKey({
  isLogin,
  codeRegion,
  codeSubRegion,
}: FeedCardsProps & { isLogin: boolean }): string {
  return [
    STORAGE_KEY.HOME_FEED_QUERY_STATE,
    isLogin ? 'auth' : 'guest',
    codeRegion ?? '',
    codeSubRegion ?? '',
  ].join(':');
}

function FeedSkeleton() {
  return (
    <div
      className={cn(
        styles.skeleton,
        'h-dvh xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200',
      )}
    />
  );
}

function FeedEndMessage() {
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <div ref={ref} className="flex flex-col items-center gap-12 py-48">
      <div className="size-56">
        {inView && (
          <Lottie
            loop={false}
            src="/assets/jsons/check.json"
            className="size-56"
          />
        )}
      </div>
      <div className="flex flex-col items-center gap-4">
        <p className="font-600 text-Gray-900 typo-body2">
          모든 피드를 확인했어요
        </p>
        <p className="font-400 text-Gray-700 typo-body4">
          새로운 소식이 올라오면 다시 알려드릴게요
        </p>
      </div>
      <Button
        asChild
        theme="sub-line"
        className="mt-4 bg-Gray-white"
        dimensions="H32"
      >
        <CustomLink href={ROUTES.RECOMMEND_POLICY_LIST.pathname}>
          나의 정책 보러 가기
        </CustomLink>
      </Button>
    </div>
  );
}

interface FeedCardsProps {
  codeRegion?: string;
  codeSubRegion?: string;
}

function FeedCards({ codeRegion, codeSubRegion }: FeedCardsProps) {
  const isLogin = useAuthStore((state) => state.isLogin === true);
  const [nextPageError, setNextPageError] = useState(false);
  const feedStateKey = createFeedStateKey({
    isLogin,
    codeRegion,
    codeSubRegion,
  });

  // 뒤로가기 복원 시에는 첫 진입 시점의 시드를 유지해야 React Query 캐시를 재사용할 수 있다.
  const initialSeenIds = useRef<SeenFeedIds>(
    (isPageReload()
      ? undefined
      : loadSeenFeedIdsFromStorage(sessionStorage, feedStateKey)) ??
      loadSeenFeedIds(SEEN_FEED_IDS_KEY),
  );

  const regionQuery = {
    ...(codeRegion && { regionCode: codeRegion }),
    ...(codeSubRegion && { subRegionCode: codeSubRegion }),
  };

  const buildBody = (page: number, seenIds: SeenFeedIds) => ({
    page,
    size: FEED_SIZE,
    ...regionQuery,
    ...toBodyFilter(seenIds),
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery({
      queryKey: [
        'feed',
        isLogin ? 'auth' : 'guest',
        codeRegion ?? '',
        codeSubRegion ?? '',
        initialSeenIds.current,
      ] as const,
      queryFn: async ({ pageParam, signal }) => {
        const sdk = isLogin ? AuthSdk.getFeedList : UnauthSdk.findFeeds;
        const { data } = await sdk({
          body: pageParam,
          signal,
          throwOnError: true,
        });

        return data;
      },
      initialPageParam: buildBody(1, initialSeenIds.current),
      getNextPageParam: ({ context }, allPages) => {
        if (!context?.hasNext) return undefined;

        const allContents = allPages.flatMap(
          (page) => page.context?.contents ?? [],
        );
        const seenIds = mergeSeenIds(
          initialSeenIds.current,
          extractSeenIds(allContents),
        );

        return buildBody(allPages.length + 1, seenIds);
      },
    });

  useEffect(() => {
    saveSeenFeedIdsToStorage(
      sessionStorage,
      feedStateKey,
      initialSeenIds.current,
    );
  }, [feedStateKey]);

  // 현재 세션까지 누적된 seen IDs를 localStorage에 저장
  useEffect(() => {
    const allContents = data.pages.flatMap(
      (page) => page.context?.contents ?? [],
    );
    const seenIds = mergeSeenIds(
      initialSeenIds.current,
      extractSeenIds(allContents),
    );
    saveSeenFeedIds(SEEN_FEED_IDS_KEY, seenIds);
  }, [data.pages]);

  const handleFetchNextPage = async () => {
    setNextPageError(false);

    try {
      await fetchNextPage({ cancelRefetch: false });
    } catch {
      setNextPageError(true);
    }
  };

  const { ref } = useInView({
    rootMargin: '200px 0px',
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage && !nextPageError) {
        handleFetchNextPage();
      }
    },
  });

  const contents = data.pages.flatMap((page) => page.context?.contents ?? []);

  if (!contents.length)
    return (
      <div className="flex items-center justify-center py-64">
        <p className="font-400 text-Gray-600 typo-body4">아직 피드가 없어요</p>
      </div>
    );

  return (
    <div className="divide-y divide-Gray-200 xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200">
      {contents.map((item, index) => (
        <FeedCard
          key={item.contentIdx ?? index}
          item={item}
          priority={index === 0}
        />
      ))}

      {!hasNextPage && contents.length > 0 && <FeedEndMessage />}

      {hasNextPage && !nextPageError && (
        <div ref={ref} className="flex items-center justify-center p-16">
          {isFetchingNextPage ? (
            <Spinner />
          ) : (
            <Button
              theme="sub-line"
              dimensions="H32"
              onClick={handleFetchNextPage}
            >
              더 불러오기
            </Button>
          )}
        </div>
      )}

      {nextPageError && (
        <div className="flex flex-col items-center gap-12 p-16">
          <p className="font-400 text-Gray-600 typo-body4">
            불러오는 중 문제가 발생했습니다.
          </p>
          <Button
            theme="sub-line"
            dimensions="H32"
            onClick={handleFetchNextPage}
          >
            다시 시도
          </Button>
        </div>
      )}
    </div>
  );
}

interface FeedListProps {
  codeRegion?: string;
  codeSubRegion?: string;
}

export const FeedList = ({ codeRegion, codeSubRegion }: FeedListProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  if (isLogin === undefined) return <FeedSkeleton />;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<FeedSkeleton />}>
        <FeedCards
          key={createFeedStateKey({
            isLogin: isLogin === true,
            codeRegion,
            codeSubRegion,
          })}
          codeRegion={codeRegion}
          codeSubRegion={codeSubRegion}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
